import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { projectsApi } from "@/api/projects";
import { isAuthenticated } from "@/auth/store";
import { Copy, Check, CheckCircle2, Loader2 } from "lucide-react";
import { PayWidget } from "@/components/pay/PayWidget";

export const Route = createFileRoute("/signup/pay/$paymentId")({
  beforeLoad: () => {
    if (!isAuthenticated()) throw redirect({ to: "/login" });
  },
  component: SignupPayPage,
});

// Mirror of the project-renewal pay page, but for the pre-project
// (pay-then-project) flow:
//   - The project doesn't exist yet — it's INSERTed by process-renewals
//     once the on-chain payment confirms. So the auth model is "the
//     user who initiated the intent," not "owner of project X."
//   - Once the intent's status flips and `project_id` is non-null, we
//     redirect to /app/projects/<that_id>.
const POLL_MS = 5_000;
const REFRESH_WATCH_MS = 15 * 60_000;

function SignupPayPage() {
  const { paymentId } = Route.useParams();
  const navigate = useNavigate();
  const [showCopied, setShowCopied] = useState(false);

  // Two coordinated polls: the intent status (carries project_id) +
  // the onchain payment state (countdown + 'received' indicator).
  const { data: intent } = useQuery({
    queryKey: ["intent", paymentId],
    queryFn: () => projectsApi.intentStatus(paymentId),
    refetchInterval: POLL_MS,
  });
  const { data: oc } = useQuery({
    queryKey: ["intent-onchain", paymentId],
    queryFn: () => projectsApi.intentOnchain(paymentId),
    refetchInterval: POLL_MS,
  });

  const refresh = useMutation({
    mutationFn: () => projectsApi.intentOnchainRefresh(paymentId),
  });

  // When the project is created, hop into the dashboard.
  useEffect(() => {
    if (intent?.project_id) {
      navigate({
        to: "/app/projects/$pid",
        params: { pid: intent.project_id },
        replace: true,
      });
    }
  }, [intent?.project_id, navigate]);

  // Silently bump the watcher every 15 min so the user can step away
  // briefly without losing detection.
  useEffect(() => {
    if (!oc || oc.status !== "pending") return;
    const t = setInterval(() => refresh.mutate(), REFRESH_WATCH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oc?.status]);

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const remainingMs = oc ? new Date(oc.expires_at).getTime() - now.getTime() : 0;
  const expired = remainingMs <= 0;

  async function copyAddress() {
    if (!oc) return;
    await navigator.clipboard.writeText(oc.public_key);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 1500);
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 py-12 px-6">
      <div>
        <h1 className="text-2xl font-semibold">Activate {intent?.intent_name ?? "your project"}</h1>
        <p className="mt-1 text-ink/65">
          Pay {oc?.amount_usdc ?? "—"} USDC on Solana. Your project will be created
          automatically once payment confirms.
        </p>
      </div>

      {!oc ? (
        <div className="card">Loading payment address…</div>
      ) : oc.status === "received" ? (
        <div className="card border-l-4 border-l-mint-500">
          <div className="flex items-start gap-3">
            <CheckCircle2 size={24} className="mt-0.5 text-mint-500" />
            <div>
              <div className="text-lg font-semibold">Payment received</div>
              <div className="mt-1 flex items-center gap-2 text-sm text-ink/65">
                <Loader2 size={14} className="animate-spin" />
                Creating your project…
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <PayWidget
            recipient={oc.public_key}
            amountUsdc={oc.amount_usdc}
            projectName={intent?.intent_name ?? "your project"}
            paymentId={paymentId}
          />

          <div className="card space-y-3">
            <div className="text-xs font-medium uppercase tracking-wider text-ink/55">
              Or send manually
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded-lg bg-ink/4 p-3 font-mono text-xs">
                {oc.public_key}
              </code>
              <button
                onClick={copyAddress}
                className="rounded-md border border-ink/15 p-2 hover:bg-cream"
                title="Copy address"
              >
                {showCopied ? <Check size={16} className="text-mint-500" /> : <Copy size={16} />}
              </button>
            </div>
            <p className="text-xs text-ink/55">
              Send only USDC on Solana — other tokens or networks will be lost.
            </p>
            <div className="flex items-baseline justify-between border-t border-ink/8 pt-3 text-sm">
              <div className="text-ink/55">
                Watching for {expired ? "—" : formatRemaining(remainingMs)}
              </div>
              {expired && (
                <button
                  onClick={() => refresh.mutate()}
                  disabled={refresh.isPending}
                  className="btn-primary"
                >
                  {refresh.isPending ? "Resuming…" : "Resume watching"}
                </button>
              )}
            </div>
          </div>
          {expired && (
            <div className="card border-l-4 border-l-peach-500 text-sm text-ink/75">
              Watch window paused — we've stopped polling for new deposits. The address still
              works; click <b>Resume watching</b> to start checking again for 20 more minutes.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
